import type { BadgeWithProgress, UserBadgeWithDetails } from '../../../lib/supabaseBadgeService'

export interface BadgesCollectionProps {
  userBadges: UserBadgeWithDetails[]
  allBadges: BadgeWithProgress[]
  showLocked?: boolean
  layout?: 'grid' | 'list'
}

export function BadgesCollection({
  userBadges,
  allBadges,
  showLocked = true,
  layout = 'grid'
}: BadgesCollectionProps) {
  // Create a map of earned badges for quick lookup
  const earnedBadgesMap = new Map(userBadges.map(ub => [ub.badge.id, ub]))

  // Calculate progress for each badge
  const getBadgeProgress = (badge: BadgeWithProgress) => {
    return badge.progress
  }


 

  const badgesToShow = showLocked ? allBadges : allBadges.filter(badge => badge.isEarned)

  if (layout === 'list') {
    return (
      <div className="space-y-4">
        {badgesToShow.map(badge => {
          const userBadge = earnedBadgesMap.get(badge.id)
          const progress = getBadgeProgress(badge)
          const isEarned = badge.isEarned
          const progressPercentage = (progress / badge.requiredValue) * 100

          return (
            <div
              key={badge.id}
              className={`
                bg-slate-800 rounded-lg p-4 border-2
                ${isEarned 
                  ? 'border-amber-500 shadow-lg shadow-amber-500/20' 
                  : 'border-slate-700 opacity-60'
                }
                transition-all duration-200
              `}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 space-x-reverse">
                  <div className="text-2xl">{badge.icon}</div>
                  <div>
                    <h3 className={`font-bold text-lg ${isEarned ? 'text-amber-400' : 'text-slate-400'}`}>
                      {badge.name}
                    </h3>
                    <p className="text-sm text-slate-300">{badge.description}</p>
                    {userBadge?.earnedAt && (
                      <p className="mt-1 text-xs text-amber-400">
                        حصلت عليها: {new Date(userBadge.earnedAt).toLocaleDateString('ar-EG')}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="text-left">
                  <div className={`text-sm font-semibold ${isEarned ? 'text-amber-400' : 'text-slate-400'}`}>
                    {badge.tier === 'bronze' && 'برونزي'}
                    {badge.tier === 'silver' && 'فضي'}
                    {badge.tier === 'gold' && 'ذهبي'}
                    {badge.tier === 'platinum' && 'بلاتيني'}
                    {badge.tier === 'diamond' && 'ألماس'}
                  </div>
                  {badge.isExclusive && (
                    <div className="mt-1 text-xs text-rose-400">حصرية</div>
                  )}
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-3">
                <div className="flex justify-between mb-1 text-xs text-slate-400">
                  <span>التقدم</span>
                  <span>{progress}/{badge.requiredValue}</span>
                </div>
                <div className="w-full h-2 rounded-full bg-slate-700">
                  <div
                    className={`
                      h-2 rounded-full transition-all duration-300
                      ${isEarned 
                        ? 'bg-gradient-to-r from-amber-400 to-amber-600' 
                        : 'bg-gradient-to-r from-slate-500 to-slate-600'
                      }
                    `}
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // Grid layout
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
      {badgesToShow.map(badge => {
        const userBadge = earnedBadgesMap.get(badge.id)
        const progress = getBadgeProgress(badge)
        const isEarned = badge.isEarned

        return (
          <div
            key={badge.id}
            className={`
              bg-slate-800 rounded-lg p-4 border-2 text-center
              ${isEarned 
                ? 'border-amber-500 shadow-lg shadow-amber-500/20' 
                : 'border-slate-700 opacity-60'
              }
              transition-all duration-200 hover:scale-105
            `}
          >
            <div className="mb-2 text-3xl">{badge.icon}</div>
            <h3 className={`font-bold text-sm ${isEarned ? 'text-amber-400' : 'text-slate-400'}`}>
              {badge.name}
            </h3>
            <p className="mt-1 text-xs text-slate-400">{badge.description}</p>
            
            <div className="mt-2">
              <div className={`text-xs font-semibold ${isEarned ? 'text-amber-400' : 'text-slate-400'}`}>
                {badge.tier === 'bronze' && 'برونزي'}
                {badge.tier === 'silver' && 'فضي'}
                {badge.tier === 'gold' && 'ذهبي'}
                {badge.tier === 'platinum' && 'بلاتيني'}
                {badge.tier === 'diamond' && 'ألماس'}
              </div>
              {badge.isExclusive && (
                <div className="mt-1 text-xs text-rose-400">حصرية</div>
              )}
              {userBadge?.earnedAt && (
                <div className="mt-1 text-xs text-amber-400">
                  {new Date(userBadge.earnedAt).toLocaleDateString('ar-EG')}
                </div>
              )}
            </div>

            {/* Progress indicator */}
            <div className="mt-3">
              <div className="text-xs text-slate-400">
                {progress}/{badge.requiredValue}
              </div>
              <div className="w-full h-1 mt-1 rounded-full bg-slate-700">
                <div
                  className={`
                    h-1 rounded-full transition-all duration-300
                    ${isEarned 
                      ? 'bg-gradient-to-r from-amber-400 to-amber-600' 
                      : 'bg-gradient-to-r from-slate-500 to-slate-600'
                    }
                  `}
                  style={{ width: `${(progress / badge.requiredValue) * 100}%` }}
                />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}